import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import { getApiUrl, getBaseUrl } from '../config/appConfig';
const getAPI = () => getApiUrl();
const getBACKEND_URL = () => getBaseUrl();
// getAPI() URL is now auto-detected

export default function PromotionalPopup({ page = "home", isLoggedIn = false }) {
  const [popups, setPopups] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [sessionDismissed, setSessionDismissed] = useState([]);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [viewCounts, setViewCounts] = useState({});

  // Get visitor ID from cookie or generate one
  const getVisitorId = useCallback(() => {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem("visitor_id", visitorId);
    }
    return visitorId;
  }, []);

  // Get permanently dismissed popups from localStorage
  const getPermanentlyDismissed = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem("dismissedPopups") || "[]");
    } catch {
      return [];
    }
  }, []);

  // Get view counts from localStorage
  const getViewCounts = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem("popupViewCounts") || "{}");
    } catch {
      return {};
    }
  }, []);

  // Save view count
  const incrementViewCount = useCallback((popupId) => {
    const counts = getViewCounts();
    counts[popupId] = (counts[popupId] || 0) + 1;
    localStorage.setItem("popupViewCounts", JSON.stringify(counts));
    setViewCounts(counts);
    return counts[popupId];
  }, [getViewCounts]);

  // Add popup to permanently dismissed list
  const addToPermanentlyDismissed = useCallback((popupId) => {
    const dismissed = getPermanentlyDismissed();
    if (!dismissed.includes(popupId)) {
      dismissed.push(popupId);
      localStorage.setItem("dismissedPopups", JSON.stringify(dismissed));
    }
  }, [getPermanentlyDismissed]);

  useEffect(() => {
    const loadPopups = async () => {
      try {
        const userType = isLoggedIn ? "logged_in" : "logged_out";
        const res = await axios.get(`${getAPI()}/cms/popups/active?page=${page}&user_type=${userType}`);
        const fetchedPopups = res.data.popups || [];
        
        // Filter out permanently dismissed popups and those exceeding max views
        const permanentlyDismissed = getPermanentlyDismissed();
        const counts = getViewCounts();
        setViewCounts(counts);
        
        const activePopups = fetchedPopups.filter(p => {
          // Check if permanently dismissed
          if (permanentlyDismissed.includes(p.popup_id)) return false;
          
          // Check if show_once and already viewed
          if (p.show_once && counts[p.popup_id] > 0) return false;
          
          // Check max views (default 5)
          const maxViews = p.max_views ?? 5;
          if (maxViews > 0 && (counts[p.popup_id] || 0) >= maxViews) return false;
          
          return true;
        });

        setPopups(activePopups);
        
        // Show first popup with delay
        if (activePopups.length > 0) {
          const firstPopup = activePopups[0];
          const delay = (firstPopup.delay_seconds || 0) * 1000;
          setTimeout(() => {
            setCurrentPopup(firstPopup);
            setDontShowAgain(false);
            // Increment view count
            incrementViewCount(firstPopup.popup_id);
          }, delay);
        }
      } catch (error) {
        console.error("Failed to fetch popups:", error);
      }
    };
    
    // Check if cookie consent was given
    const consent = localStorage.getItem("cookie_consent");
    if (consent) {
      loadPopups();
    }
  }, [page, isLoggedIn, getPermanentlyDismissed, getViewCounts, incrementViewCount]);

  const dismissPopup = (popupId) => {
    const popup = popups.find(p => p.popup_id === popupId);
    
    // If "don't show again" is checked OR popup has show_once, permanently dismiss
    if (dontShowAgain || popup?.show_once) {
      addToPermanentlyDismissed(popupId);
    }
    
    // Record dismissal on server
    axios.post(`${getAPI()}/cms/popups/view`, {
      popup_id: popupId,
      dismissed: true,
      dont_show_again: dontShowAgain
    }, { withCredentials: true }).catch(() => {});
    
    // Add to session dismissed
    const newSessionDismissed = [...sessionDismissed, popupId];
    setSessionDismissed(newSessionDismissed);
    setCurrentPopup(null);
    setDontShowAgain(false);
    
    // Find next popup that hasn't been dismissed
    const permanentlyDismissed = getPermanentlyDismissed();
    const allDismissed = [...new Set([...newSessionDismissed, ...permanentlyDismissed])];
    const counts = getViewCounts();
    
    const remaining = popups.filter(p => {
      if (allDismissed.includes(p.popup_id)) return false;
      const maxViews = p.max_views ?? 5;
      if (maxViews > 0 && (counts[p.popup_id] || 0) >= maxViews) return false;
      return true;
    });
    
    if (remaining.length > 0) {
      const nextPopup = remaining[0];
      const delay = (nextPopup.delay_seconds || 0) * 1000;
      setTimeout(() => {
        setCurrentPopup(nextPopup);
        setDontShowAgain(false);
        incrementViewCount(nextPopup.popup_id);
      }, Math.max(delay, 500));
    }
  };

  const handleCtaClick = (popup) => {
    // Record click
    axios.post(`${getAPI()}/cms/popups/click?popup_id=${popup.popup_id}`, {}, { withCredentials: true }).catch(() => {});
    
    if (popup.cta_link) {
      if (popup.cta_link.startsWith("http")) {
        window.open(popup.cta_link, "_blank");
      } else {
        window.location.href = popup.cta_link;
      }
    }
    dismissPopup(popup.popup_id);
  };

  if (!currentPopup) return null;

  const positionClasses = {
    center: "items-center justify-center",
    top: "items-start justify-center pt-20",
    bottom: "items-end justify-center pb-20",
    "bottom-right": "items-end justify-end p-6",
    "bottom-left": "items-end justify-start p-6"
  };

  const typeColors = {
    announcement: "from-blue-600 to-indigo-600",
    promotion: "from-violet-600 to-purple-600",
    exit_intent: "from-red-500 to-orange-500",
    welcome: "from-green-500 to-emerald-500",
    newsletter: "from-pink-500 to-rose-500",
    feedback: "from-amber-500 to-orange-500"
  };

  const maxViews = currentPopup.max_views ?? 5;
  const currentViews = viewCounts[currentPopup.popup_id] || 1;

  return (
    <div className={`fixed inset-0 z-50 flex ${positionClasses[currentPopup.position] || positionClasses.center}`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => currentPopup.dismissible && dismissPopup(currentPopup.popup_id)}
      />
      
      {/* Popup Content */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Dismiss button */}
        {currentPopup.dismissible && (
          <button
            onClick={() => dismissPopup(currentPopup.popup_id)}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Image */}
        {currentPopup.image_url && (
          <div className="w-full h-48 overflow-hidden">
            <img 
              src={currentPopup.image_url.startsWith("http") ? currentPopup.image_url : `${getBACKEND_URL()}${currentPopup.image_url}`}
              alt={currentPopup.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Header gradient bar (if no image) */}
        {!currentPopup.image_url && (
          <div className={`h-2 bg-gradient-to-r ${typeColors[currentPopup.popup_type] || typeColors.announcement}`} />
        )}

        {/* Content */}
        <div className="p-6">
          {/* Type badge */}
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${typeColors[currentPopup.popup_type] || typeColors.announcement} text-white`}>
              {currentPopup.popup_type === "announcement" && "Announcement"}
              {currentPopup.popup_type === "promotion" && "Special Offer"}
              {currentPopup.popup_type === "exit_intent" && "Wait!"}
              {currentPopup.popup_type === "welcome" && "Welcome"}
              {currentPopup.popup_type === "newsletter" && "Newsletter"}
              {currentPopup.popup_type === "feedback" && "Feedback"}
            </span>
            {maxViews > 0 && (
              <span className="text-xs text-gray-400">
                {currentViews} of {maxViews} views
              </span>
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {currentPopup.title}
          </h2>

          {/* Content - HTML sanitized via DOMPurify to prevent XSS */}
          <div 
            className="text-gray-600 text-sm mb-4 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentPopup.content || '', {
              ALLOWED_TAGS: ['p','br','strong','em','b','i','u','a','ul','ol','li','span','div','h1','h2','h3','h4','h5','h6'],
              ALLOWED_ATTR: ['href','target','rel','class','style']
            }) }}
          />

          {/* Don't show again checkbox */}
          {currentPopup.dismissible && (currentPopup.show_dont_show_again !== false) && !currentPopup.show_once && (
            <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm text-gray-600 hover:text-gray-900">
              <Checkbox
                checked={dontShowAgain}
                onCheckedChange={(checked) => setDontShowAgain(checked)}
              />
              Don&apos;t show this again
            </label>
          )}

          {/* CTA Button */}
          {currentPopup.cta_text && (
            <div className="flex gap-3">
              <Button
                className={`flex-1 bg-gradient-to-r ${typeColors[currentPopup.popup_type] || typeColors.announcement} text-white hover:opacity-90`}
                onClick={() => handleCtaClick(currentPopup)}
              >
                {currentPopup.cta_text}
                {currentPopup.cta_link?.startsWith("http") && (
                  <ExternalLink className="w-4 h-4 ml-2" />
                )}
              </Button>
              
              {currentPopup.dismissible && (
                <Button
                  variant="outline"
                  onClick={() => dismissPopup(currentPopup.popup_id)}
                >
                  Maybe Later
                </Button>
              )}
            </div>
          )}

          {/* If no CTA but dismissible, show close button */}
          {!currentPopup.cta_text && currentPopup.dismissible && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => dismissPopup(currentPopup.popup_id)}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
